/**
 * Manager for visual effects configuration state with persistence and EventBus integration.
 * Single source of truth for hover/selection visual effects (halo, outlineEdges, incandescent, edgeRenderer).
 */

import { Observable } from "@babylonjs/core";
import { BaseManager } from "../core/BaseManager.js";
import type { VisualEffectConfig } from "../interaction/types.js";

/**
 * Configuration for visual effects (hover and selection states)
 */
export interface VisualEffectsConfig {
  stageId: string;
  hover: VisualEffectConfig;
  selection: VisualEffectConfig;
}

/**
 * Default visual effect settings
 */
const DEFAULT_HOVER_EFFECTS: VisualEffectConfig = {
  edgeRenderer: true,
  halo: false,
  incandescent: false,
  outlineEdges: false,
};

const DEFAULT_SELECTION_EFFECTS: VisualEffectConfig = {
  edgeRenderer: false,
  halo: false,
  incandescent: false,
  outlineEdges: false,
};

/**
 * Default visual effects configurations per stage type
 */
const DEFAULT_CONFIGS: Record<string, Omit<VisualEffectsConfig, "stageId">> = {
  bob: {
    hover: { ...DEFAULT_HOVER_EFFECTS },
    selection: { ...DEFAULT_SELECTION_EFFECTS },
  },
  gem: {
    hover: { ...DEFAULT_HOVER_EFFECTS },
    selection: { ...DEFAULT_SELECTION_EFFECTS },
  },
  rune: {
    hover: { ...DEFAULT_HOVER_EFFECTS, edgeRenderer: false },
    selection: { ...DEFAULT_SELECTION_EFFECTS },
  },
};

/**
 * localStorage key prefix for visual effects configs
 */
const STORAGE_KEY_PREFIX = "tony.nl/visual-effects-config/";

/**
 * Manages visual effects configuration state with Observable notifications,
 * localStorage persistence, and EventBus integration.
 *
 * Used by both React demo pages (via useVisualEffectsConfig hook) and
 * Inspector v2 (via useObservableState).
 */
export class VisualEffectsConfigManager extends BaseManager {
  private readonly _configChangedObservable =
    new Observable<VisualEffectsConfig | null>();
  private currentConfig: VisualEffectsConfig | null = null;

  /**
   * Observable that fires when config changes.
   * Subscribe with: manager.configChangedObservable.add(callback)
   */
  get configChangedObservable(): Observable<VisualEffectsConfig | null> {
    return this._configChangedObservable;
  }

  /**
   * Current visual effects config. Alias for getCurrentConfig() to support
   * useObservableState pattern: `useObservableState(() => manager.config, ...)`
   */
  get config(): VisualEffectsConfig | null {
    return this.currentConfig;
  }

  /**
   * Implementation-specific initialization
   */
  protected async onInitialize(): Promise<void> {
    this.emit("visualEffectsConfigManagerReady", {});
  }

  /**
   * Implementation-specific disposal
   */
  protected onDispose(): void {
    this._configChangedObservable.clear();
    this.currentConfig = null;
  }

  /**
   * Get the current visual effects config
   */
  getCurrentConfig(): VisualEffectsConfig | null {
    return this.currentConfig;
  }

  /**
   * Get default config for a stage type
   */
  getDefaultConfig(stageId: string): VisualEffectsConfig {
    const defaults = DEFAULT_CONFIGS[stageId] ?? {
      hover: { ...DEFAULT_HOVER_EFFECTS },
      selection: { ...DEFAULT_SELECTION_EFFECTS },
    };
    return { stageId, ...defaults };
  }

  /**
   * Called when a stage is loaded. Loads config from localStorage or defaults.
   * @param stageId The stage ID that was loaded
   */
  onStageLoaded(stageId: string): void {
    this.ensureNotDisposed();

    // Try to load from localStorage
    const stored = this.loadFromStorage(stageId);
    if (stored) {
      this.currentConfig = stored;
    } else {
      // Use defaults
      this.currentConfig = this.getDefaultConfig(stageId);
    }

    // Notify observers
    this._configChangedObservable.notifyObservers(this.currentConfig);
  }

  /**
   * Update the current config with partial values.
   * Persists to localStorage and emits visualEffectsConfigChanged event.
   * @param partial Partial config to merge
   */
  updateConfig(partial: Partial<VisualEffectsConfig>): void {
    this.ensureNotDisposed();

    if (!this.currentConfig) {
      console.warn("[VisualEffectsConfigManager] No current config to update");
      return;
    }

    // Merge partial into current (deep merge for nested objects)
    this.currentConfig = {
      ...this.currentConfig,
      hover: partial.hover
        ? { ...this.currentConfig.hover, ...partial.hover }
        : this.currentConfig.hover,
      selection: partial.selection
        ? { ...this.currentConfig.selection, ...partial.selection }
        : this.currentConfig.selection,
      // Ensure stageId doesn't change
      stageId: this.currentConfig.stageId,
    };

    // Persist to localStorage
    this.saveToStorage(this.currentConfig);

    // Notify Observable subscribers (React hooks, Inspector)
    this._configChangedObservable.notifyObservers(this.currentConfig);

    // Emit EventBus event
    this.emit("visualEffectsConfigChanged", this.currentConfig);
  }

  /**
   * Set the full config (replaces current)
   * @param config The new config
   */
  setConfig(config: VisualEffectsConfig): void {
    this.ensureNotDisposed();

    this.currentConfig = config;

    // Persist to localStorage
    this.saveToStorage(config);

    // Notify Observable subscribers
    this._configChangedObservable.notifyObservers(this.currentConfig);

    // Emit EventBus event
    this.emit("visualEffectsConfigChanged", this.currentConfig);
  }

  /**
   * Reset config to defaults for the current stage
   */
  resetToDefaults(): void {
    this.ensureNotDisposed();

    if (!this.currentConfig) {
      console.warn("[VisualEffectsConfigManager] No current config to reset");
      return;
    }

    const stageId = this.currentConfig.stageId;

    // Clear from localStorage
    this.clearStorage(stageId);

    // Reset to defaults
    this.currentConfig = this.getDefaultConfig(stageId);

    // Notify Observable subscribers
    this._configChangedObservable.notifyObservers(this.currentConfig);

    // Emit EventBus event
    this.emit("visualEffectsConfigChanged", this.currentConfig);
  }

  /**
   * Load config from localStorage
   */
  private loadFromStorage(stageId: string): VisualEffectsConfig | null {
    try {
      const key = STORAGE_KEY_PREFIX + stageId;
      const stored = localStorage.getItem(key);
      if (!stored) return null;

      const parsed = JSON.parse(stored) as Partial<VisualEffectsConfig>;

      // Validate and merge with defaults
      const defaults = this.getDefaultConfig(stageId);
      return {
        ...defaults,
        hover: parsed.hover
          ? { ...defaults.hover, ...parsed.hover }
          : defaults.hover,
        selection: parsed.selection
          ? { ...defaults.selection, ...parsed.selection }
          : defaults.selection,
        stageId, // Ensure stageId is correct
      };
    } catch {
      console.warn(
        `[VisualEffectsConfigManager] Failed to load config for ${stageId}`,
      );
      return null;
    }
  }

  /**
   * Save config to localStorage
   */
  private saveToStorage(config: VisualEffectsConfig): void {
    try {
      const key = STORAGE_KEY_PREFIX + config.stageId;
      const toStore: Partial<VisualEffectsConfig> = {
        hover: config.hover,
        selection: config.selection,
      };
      localStorage.setItem(key, JSON.stringify(toStore));
    } catch {
      console.warn(
        `[VisualEffectsConfigManager] Failed to save config for ${config.stageId}`,
      );
    }
  }

  /**
   * Clear config from localStorage
   */
  private clearStorage(stageId: string): void {
    try {
      const key = STORAGE_KEY_PREFIX + stageId;
      localStorage.removeItem(key);
    } catch {
      // Ignore errors
    }
  }
}
